import React, { useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Prod01 } from '../../constants/ActionType';
import { formatDateTime } from '../../utils/dateFormat';

// Clinical-note governance UI (owner rules 2026-09-26): a note belongs to its author and is OPEN for
// 72h (in-place edits, Sign & lock, void); afterwards every change is an append-only AMENDMENT with a
// reason. Physicians / Super Admin always amend. Lead Nurse, Org Manager, billing, accounting: view only.
// Talks to /v1/notes/:noteType/:noteId on the beta backend.

const AMEND_ALWAYS = ['doctor', 'admin'];
const VIEW_ONLY = ['HrLead', 'adminStaff', 'billing', 'accounting'];
const when = (d) => (d ? formatDateTime(d) : '');

export const notePermissions = (row, user) => {
    const role = user?.role;
    const isAuthor = !!user?._id && String(row.noteAuthorId || row.loggedById || '') === String(user._id);
    const locked = row.noteStatus === 'locked';
    if (row.voided || !role || VIEW_ONLY.includes(role)) {
        return { edit: false, amend: false, lock: false, void: false, minutes: false, isAuthor, locked };
    }
    const superAdmin = role === 'admin';
    return {
        isAuthor,
        locked,
        edit: isAuthor && !locked && !AMEND_ALWAYS.includes(role),
        amend: AMEND_ALWAYS.includes(role) || (isAuthor && locked),
        lock: isAuthor && !locked,
        void: (isAuthor && !locked) || superAdmin,
        minutes: row.minutes !== undefined && ((isAuthor && !locked) || superAdmin),
    };
};

export const NoteBadges = ({ row }) => (
    <>
        <span className={`badge ms-1 ${row.noteStatus === 'locked' ? 'bg-secondary' : 'bg-success'}`} style={{ fontSize: 10 }}
            title={row.noteStatus === 'locked' ? `Locked ${when(row.lockedAt)} by ${row.lockedBy === 'auto-72h' ? 'system (72h)' : row.signedBy || row.lockedBy || ''}` : `Open until ${when(row.locksAt)}`}>
            {row.noteStatus === 'locked' ? 'locked' : 'open'}
        </span>
        {row.signedBy && <span className="badge bg-dark ms-1" style={{ fontSize: 10 }}>signed {row.signedBy}</span>}
        {row.amendmentCount > 0 && (
            <span className="badge bg-warning text-dark ms-1" style={{ fontSize: 10 }}>
                Amended by {row.lastAmendedBy || 'unknown'} on {when(row.lastAmendedAt)}
            </span>
        )}
        {row.voided && <span className="badge bg-danger ms-1" style={{ fontSize: 10 }} title={row.voidReason}>void: {row.voidReason}</span>}
    </>
);

// One governed note row: latest text (struck through when voided), badges, actions and history.
export const ClinicalNoteRow = ({ row, user, onChanged, children, as: Tag = 'li' }) => {
    const [mode, setMode] = useState(null); // 'edit' | 'amend' | 'void' | 'minutes' | 'history'
    const [text, setText] = useState(row.note || '');
    const [reason, setReason] = useState('');
    const [minutes, setMinutes] = useState(row.minutes);
    const [history, setHistory] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const perm = notePermissions(row, user);
    const url = `${Prod01}/notes/${row.noteType || 'entry'}/${row.id}`;

    const run = async (fn) => {
        setBusy(true); setError(null);
        try { await fn(); setMode(null); setReason(''); if (onChanged) await onChanged(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setBusy(false); }
    };
    const save = () => run(() => axios.put(url, { text: text.trim(), reason: mode === 'amend' ? reason.trim() : undefined }));
    const lock = () => run(() => axios.post(`${url}/lock`, {}));
    const doVoid = () => run(() => axios.post(`${url}/void`, { reason: reason.trim() }));
    const saveMinutes = () => run(() => axios.post(`${url}/minutes`, { minutes: Number(minutes), reason: reason.trim() }));
    const showHistory = async () => {
        setError(null);
        try { const r = await axios.get(url); setHistory(r.data); setMode('history'); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
    };

    return (
        <Tag style={{ display: 'block', borderBottom: '1px solid #eee', padding: '2px 0' }}>
            <span style={row.voided ? { textDecoration: 'line-through', color: '#999' } : undefined}>{children}</span>
            <NoteBadges row={row} />
            <span className="ms-1">
                {perm.edit && <button className="btn btn-link btn-sm p-0 me-1" style={{ fontSize: 11 }} onClick={() => { setText(row.note || ''); setMode('edit'); }}>edit</button>}
                {perm.amend && <button className="btn btn-link btn-sm p-0 me-1" style={{ fontSize: 11 }} onClick={() => { setText(row.note || ''); setMode('amend'); }}>amend</button>}
                {perm.lock && <button className="btn btn-link btn-sm p-0 me-1" style={{ fontSize: 11 }} onClick={lock} disabled={busy}>sign &amp; lock</button>}
                {perm.minutes && <button className="btn btn-link btn-sm p-0 me-1" style={{ fontSize: 11 }} onClick={() => setMode('minutes')}>correct minutes</button>}
                {perm.void && <button className="btn btn-link btn-sm p-0 me-1 text-danger" style={{ fontSize: 11 }} onClick={() => setMode('void')}>void</button>}
                <button className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={mode === 'history' ? () => setMode(null) : showHistory}>{mode === 'history' ? 'hide history' : 'history'}</button>
            </span>
            {error && <span className="text-danger d-block" style={{ fontSize: 11 }}>{error}</span>}
            {(mode === 'edit' || mode === 'amend') && (
                <div className="d-flex gap-1 mt-1">
                    <input className="form-control form-control-sm" value={text} onChange={e => setText(e.target.value)} aria-label="Note text" />
                    {mode === 'amend' && <input className="form-control form-control-sm" placeholder="Reason for amendment (required)" value={reason} onChange={e => setReason(e.target.value)} />}
                    <button className="btn btn-primary btn-sm text-nowrap" onClick={save} disabled={busy || !text.trim() || (mode === 'amend' && !reason.trim())}>{mode === 'amend' ? 'Save amendment' : 'Save'}</button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setMode(null)}>Cancel</button>
                </div>
            )}
            {mode === 'void' && (
                <div className="d-flex gap-1 mt-1">
                    <input className="form-control form-control-sm" placeholder="Void reason (required)" value={reason} onChange={e => setReason(e.target.value)} />
                    <button className="btn btn-danger btn-sm" onClick={doVoid} disabled={busy || !reason.trim()}>Void note</button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setMode(null)}>Cancel</button>
                </div>
            )}
            {mode === 'minutes' && (
                <div className="d-flex gap-1 mt-1">
                    <input type="number" min="1" max="600" className="form-control form-control-sm" style={{ width: 90 }} value={minutes} onChange={e => setMinutes(e.target.value)} aria-label="Corrected minutes" />
                    <input className="form-control form-control-sm" placeholder="Correction reason (required)" value={reason} onChange={e => setReason(e.target.value)} />
                    <button className="btn btn-primary btn-sm text-nowrap" onClick={saveMinutes} disabled={busy || !reason.trim() || !(Number(minutes) >= 1)}>Save correction</button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setMode(null)}>Cancel</button>
                </div>
            )}
            {mode === 'history' && history && (
                <div className="mt-1 p-1 rounded" style={{ background: '#f8f9fa', fontSize: 11 }}>
                    <div><b>Original (locked):</b> {history.originalText}</div>
                    <div className="text-muted">
                        Author {history.noteAuthorName || '—'} · created {when(history.noteCreatedAt)}
                        {history.noteStatus === 'locked' && ` · locked ${when(history.lockedAt)} by ${history.lockedBy === 'auto-72h' ? 'system (72h)' : history.signedBy || history.lockedBy}`}
                    </div>
                    {(history.history || []).length === 0 && <div className="text-muted">No amendments or corrections.</div>}
                    {(history.history || []).map(h => (
                        <div key={`${h.event}-${h.at}`}>
                            {when(h.at)} · <b>{h.event}</b> by {h.by}{h.credentials ? `, ${h.credentials}` : ''} ({h.role})
                            {h.event === 'amend' && <> · “{h.previousText}” → “{h.newText}”</>}
                            {h.event === 'minutes' && <> · {h.previousMinutes}m → {h.newMinutes}m</>}
                            {h.reason && <> · reason: {h.reason}</>}
                        </div>
                    ))}
                </div>
            )}
        </Tag>
    );
};

// Embedded HealthData reading comment (patientsReadingComment) rendered as a governed note.
export const ReadingCommentNote = ({ note, user, onChanged }) => {
    const author = note?.conclusion_hr_id || note?.conclusion_doctor_id;
    const row = {
        ...note,
        id: note?._id,
        noteType: 'reading',
        note: note?.conclusion,
        noteAuthorId: note?.noteAuthorId || (author && author._id),
        noteStatus: note?.noteStatus === 'locked' || (note?.dateTime && Date.now() - new Date(note.dateTime).getTime() >= 72 * 3600 * 1000) ? 'locked' : 'open',
    };
    if (!row.id) return <span>{note?.conclusion}</span>;
    return <ClinicalNoteRow as="span" row={row} user={user} onChanged={onChanged}>{note?.conclusion}</ClinicalNoteRow>;
};

// 'Enroll in PCM?' step: consent (method + date), enrollment start date; enrolling staff comes from the session.
export const PcmEnrollPrompt = ({ patientId, prompt, onEnrolled, onDismiss }) => {
    const program = prompt?.program || 'PCM';
    const [method, setMethod] = useState('Verbal');
    const [consentDate, setConsentDate] = useState(moment().format('YYYY-MM-DD'));
    const [startedAt, setStartedAt] = useState(moment().format('YYYY-MM-DD'));
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const consentOnFile = !!prompt?.consentOnFile;

    const enroll = async () => {
        setBusy(true); setError(null);
        try {
            await axios.post(`${Prod01}/billing/enrollment/${patientId}`, {
                program, startedAt, note: note.trim() || `Enrolled in ${program} from prompt`,
                qualifies: true, qualificationBasis: 'Confirmed by Provider / Nurse at enrollment prompt',
                consent: consentOnFile ? undefined : { method, consentDate },
            });
            if (onEnrolled) await onEnrolled();
        } catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="alert alert-info py-2 mt-2 mb-1" style={{ fontSize: 13 }}>
            <b>Enroll first: {program}</b> {typeof prompt?.chronicConditionCount === 'number' && <span>Patient has {prompt.chronicConditionCount} qualifying chronic condition(s) and is not enrolled in {program}. </span>}
            {program} minutes can only be logged once the patient qualifies, consent is recorded and an enrollment date is set.
            <div className="d-flex flex-wrap gap-1 mt-1 align-items-center">
                {!consentOnFile && (
                    <>
                        <select className="form-select form-select-sm" style={{ width: 120 }} value={method} onChange={e => setMethod(e.target.value)} aria-label="Consent method">
                            {['Verbal', 'Written', 'Electronic'].map(m => <option key={m}>{m}</option>)}
                        </select>
                        <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={consentDate} max={moment().format('YYYY-MM-DD')} onChange={e => setConsentDate(e.target.value)} aria-label="Consent date" />
                    </>
                )}
                {consentOnFile && <span className="text-muted">{program} consent on file.</span>}
                <span style={{ fontSize: 12 }}>Enrollment date</span>
                <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={startedAt} max={moment().format('YYYY-MM-DD')} onChange={e => setStartedAt(e.target.value)} aria-label="Enrollment date" />
                <input className="form-control form-control-sm" style={{ width: 200 }} placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} />
                <button className="btn btn-primary btn-sm" onClick={enroll} disabled={busy || !startedAt || (!consentOnFile && !consentDate)}>Enroll in {program}</button>
                {onDismiss && <button className="btn btn-outline-secondary btn-sm" onClick={onDismiss}>Not now</button>}
            </div>
            {error && <div className="text-danger mt-1" style={{ fontSize: 12 }}>{error}</div>}
        </div>
    );
};
